import { Button, Grid, Slider, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import SummaryIndicator from './A3PComponent/SummaryIndicator'
import { getRiskOfShortageLabel, getSitu } from '../utils/A3PUtils'
import RecommendationIndicator from './A3PComponent/RecommendationIndicator'
import SmallIndicator from './A3PComponent/SmallIndicator'
import VPIndicator from './A3PComponent/VPIndicator'
import { formatSign, hasValue } from 'utils/NumberUtil'
import { formatMilliers } from 'utils/StringUtil'
import { darkBlue, disabledColor, lightGrey } from 'utils/constants/ColorTheme'
import { clamp, round, capitalize, toUpper, orderBy, template } from 'lodash'
import ChangeVPLevelDialog from './A3PComponent/ChangeVPLevelDialog'
import ManagementNeedsChart from './A3PComponent/ManagementNeedsChart'
import FollowAndPredictionConsoChart from './A3PComponent/FollowAndPredictionConsoChart'
import Select from 'components/forms/Select'
import { DEPTH } from 'utils/PiezometryUtils'
import { HYDROMETER_HEIGHT_TYPE } from 'hydrometry/constants/HydrometryConstants'
import useGlobalParameter from 'utils/customHook/useGlobalParameter'
import FollowAndPredictionResourceChart from './A3PComponent/FollowAndPredictionResourceChart'
import AgriAction from 'agriAdministration/actions/AgriAction'
import ProgressCard from 'components/card/ProgressCard'
import { NoDataCard } from 'components/card/NoDataCard'

const TerritoryA3PDetail = ({}) => {
    const [openPopupSelectionVP, setOpenPopupSelectionVP] = useState(false)

    const params = useParams()
    const dispatch = useDispatch()

    const {
        A3PDetails: {
            managementUnitsDetails = [],
            alertLevels = [],
        },
    } = useSelector(store => ({
        A3PDetails: store.AgriReducer.A3PDetails,
    }), shallowEqual)

    const nbMoisClimato = useGlobalParameter('A3P_nb_mois', v => v ? Number(v) : undefined)

    const managementUnitDetails = useMemo(() => managementUnitsDetails.find(({ managementCode }) => managementCode === parseInt(params.id)), [managementUnitsDetails, params.id])
    const isManagementUnitFound = !!managementUnitDetails

    const {
        consumptionTrajectoryDeviation,
        managementUnitName,
        percentageAutorisationConsumed,
        volumeConsumed,
        recommendation,
        riskOfShortage,
        waterDispo = {},
        withdrawableVolume,
        vpLevelValues = [],
        iaeauResults,
        linkStationsDetails = [],
    } = managementUnitDetails ?? {}

    const [isStationsLoaded, setIsStationsLoaded] = useState(!!linkStationsDetails.length)

    useEffect(() => {
        if (linkStationsDetails.length || !isManagementUnitFound) {
            setIsStationsLoaded(true)
            return
        }
        setIsStationsLoaded(false)
        dispatch(AgriAction.fetchA3PStationsDetails(params.id)).then(() => setIsStationsLoaded(true))
    }, [dispatch, params.id, linkStationsDetails.length, isManagementUnitFound])

    const formattedLinkStationsDetails = useMemo(() => orderBy(linkStationsDetails.map(linkStationDetails => {
        const dataTypeName = linkStationDetails.dataType === DEPTH ? i18n.depth : linkStationDetails.dataType === HYDROMETER_HEIGHT_TYPE ? i18n.height : i18n.flow
        const formattedName = `[${toUpper(dataTypeName)}] ${linkStationDetails.name || linkStationDetails.code}`
        return ({
            ...linkStationDetails,
            name: formattedName,
        })
    }), 'dataType', 'desc'), [linkStationsDetails])

    const vpValue = vpLevelValues[withdrawableVolume]
    const estimatedNeed = useMemo(() => iaeauResults?.graphe_thermometre?.besoin_total_estime, [iaeauResults])
    const coverageRateOfNeeds = useMemo(() => hasValue(vpValue) && estimatedNeed > 0 ? (vpValue / estimatedNeed) * 100 : undefined, [vpValue, estimatedNeed])
    const [selectedStation, setSelectedStation] = useState(undefined)

    useEffect(() => {
        const firstLinkedStation = formattedLinkStationsDetails[0]
        setSelectedStation(firstLinkedStation && { idStation: firstLinkedStation.idStation, dataType: firstLinkedStation.dataType })
    }, [formattedLinkStationsDetails])

    const selectedStationDetails = useMemo(() => linkStationsDetails.find(({ idStation, dataType }) => idStation === selectedStation?.idStation && dataType === selectedStation?.dataType), [linkStationsDetails, selectedStation])

    useTitle(() => [{
        title: i18n.steering,
        href: 'territory',
    }, {
        title: i18n.A3P,
        href: 'territory/A3P',
    }, {
        title: managementUnitName,
        href: `territory/A3P/${params.id}`,
    }], [managementUnitName, params.id])

    const recommendationContent = [{
        title: i18n.VPOnGoing,
        VPLevel: withdrawableVolume,
    }, {
        title: i18n.VPRecommended,
        color: 'white',
        VPLevel: withdrawableVolume,
    }]

    /* style sans erreur {
        title: i18n.VPRecommended,
        color: 'white',
        VPLevel: withdrawableVolume,
    } */

    /* style avec erreur {
        title: i18n.VPRecommended,
        color: 'reinforcedWarningStatus.main',
        borderColor: getDarkerHex(reinforcedWarningColor, 25),
        VPLevel: withdrawableVolume + 1,
    } */

    const needCoverageColor = useMemo(() => (coverageRateOfNeeds >= 100 ?
        'validStatus'
        :
        coverageRateOfNeeds < 80 ?
            'errorStatus'
            :
            undefined
    ), [coverageRateOfNeeds])

    const trajDeviationColor = useMemo(() => (consumptionTrajectoryDeviation <= -10 ?
        'validStatus'
        :
        consumptionTrajectoryDeviation > 10 ?
            'errorStatus'
            :
            undefined
    ), [consumptionTrajectoryDeviation])

    const adjustSliderLabelPosition = (pos) => clamp(pos, 5, 93)
    const hasPercentageConsumed = hasValue(percentageAutorisationConsumed)
    const sliderValue = hasPercentageConsumed ? clamp(percentageAutorisationConsumed, 0, 100) : 0
    const leftPos = adjustSliderLabelPosition(sliderValue / 2)
    const rightPos = adjustSliderLabelPosition(sliderValue + (100 - sliderValue) / 2)
    const hasVolumeConsumed = hasValue(volumeConsumed)
    const canComputeRemainingVolume = hasValue(vpValue) && hasVolumeConsumed
    const getAlertLabel = restrictionId => (hasValue(restrictionId) && alertLevels.find(({ idAlert }) => idAlert === restrictionId)?.alertLabel) || i18n.normal

    if (!isManagementUnitFound) {
        return <NoDataCard />
    }

    return (
        <Stack spacing={1}>
            <Grid container spacing={1}>
                <Grid container size={3}>
                    <SummaryIndicator
                        size={12}
                        title={i18n.climate}
                        subTitle={hasValue(nbMoisClimato) ? template(i18n.observedOverTheLastXMonths)({ nbMonths: nbMoisClimato }) : undefined}
                        content={
                            <Grid container alignItems={'baseline'}>
                                <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{capitalize(iaeauResults?.SPEI_UG?.interpretation || i18n.unknown)}</Typography>
                            </Grid>
                        }
                        bgColor={'white'}
                    />
                    <SummaryIndicator
                        size={12}
                        title={i18n.probOfExceedingThreshold}
                        content={
                            <Grid container alignItems={'baseline'}>
                                <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{getRiskOfShortageLabel(riskOfShortage)}</Typography>
                            </Grid>
                        }
                        bgColor={'white'}
                    />
                </Grid>
                <SummaryIndicator
                    size={3}
                    title={i18n.ressourceStatus}
                    secondaryContent={
                        <Stack>
                            <Stack direction='row' spacing={1}>
                                <Grid size='grow' sx={{ fontWeight: 'bold' }}>{i18n.surfaceWater}</Grid>
                                <Typography color={disabledColor}>
                                    {getAlertLabel(waterDispo.superficial?.restrictionId)}
                                </Typography>
                            </Stack>
                            {waterDispo.superficial && getSitu(waterDispo.superficial)}
                            <Stack direction='row' spacing={1} paddingTop={1}>
                                <Grid size='grow' sx={{ fontWeight: 'bold' }}>{i18n.undergroundWater}</Grid>
                                <Typography color={disabledColor}>
                                    {getAlertLabel(waterDispo.underground?.restrictionId)}
                                </Typography>
                            </Stack>
                            {waterDispo.underground && getSitu(waterDispo.underground)}
                        </Stack>
                    }
                    bgColor={'white'}
                />
                <SummaryIndicator
                    size={6}
                    title={
                        <Grid container spacing={1} alignItems={'center'}>
                            <Typography color='third' fontWeight={'bold'}>{`${i18n.advise} : `}</Typography>
                            <RecommendationIndicator indicator={recommendation} />
                        </Grid>
                    }
                    bgColor={'white'}
                    actions={[
                        <Button
                            key={i18n.editVP}
                            variant={'contained'}
                            onClick={(e) => {
                                e.stopPropagation()
                                setOpenPopupSelectionVP(true)
                            }}
                            sx={{ textTransform: 'capitalize', fontSize: '13px', borderRadius: 10 }}
                        >
                            {i18n.editVP}
                        </Button>,
                    ]}
                    secondaryContent={
                        <Grid container size={12} spacing={2}>
                            {recommendationContent.map(({ title, color, borderColor, VPLevel }) => (
                                <SummaryIndicator
                                    key={title}
                                    size={6}
                                    outlined
                                    title={<SmallIndicator bgcolor='white'>{title}</SmallIndicator>}
                                    secondaryContent={<VPIndicator bigDisplay indicator={VPLevel} volume={vpLevelValues[VPLevel]} />}
                                    bgColor={color}
                                    borderColor={borderColor}
                                />
                            ))}
                        </Grid>
                    }
                />
            </Grid>
            <SummaryIndicator
                size={12}
                title={i18n.samplesReport}
                bgColor='white'
                content={<Grid size={12} sx={{ padding: '2rem 1rem 0 1rem' }}>
                    <Slider
                        value={sliderValue}
                        min={0}
                        max={100}
                        disabled
                        valueLabelDisplay='on'
                        valueLabelFormat={() => (hasPercentageConsumed ? `${percentageAutorisationConsumed}%` : '-')}
                        marks={[
                            {
                                value: leftPos,
                                label: (
                                    <Stack direction={'row'} spacing={0.8} alignItems={'center'}>
                                        {`${i18n.sampledVolumeNoUnit} :`}
                                        <Typography fontWeight={'bold'} fontSize={18}>{hasVolumeConsumed ? `${formatMilliers(round(volumeConsumed))} m³` : '-'}</Typography>
                                    </Stack>
                                ),
                            },
                            {
                                value: rightPos,
                                label: (
                                    <Stack direction={'row'} spacing={0.8} alignItems={'center'}>
                                        {`${i18n.volumeRemaining} :`}
                                        <Typography fontWeight={'bold'} fontSize={18}>{canComputeRemainingVolume ? `${formatMilliers(round(vpValue - volumeConsumed))} m³` : '-'}</Typography>
                                    </Stack>
                                ),
                            },
                        ]}
                        sx={{
                            '& .MuiSlider-rail': {
                                color: lightGrey,
                                borderRadius: 10,
                                height: '8px',
                            },

                            '& .MuiSlider-track': {
                                color: darkBlue,
                                borderRadius: 10,
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                                height: '8px',
                            },

                            '& .MuiSlider-thumb': {
                                width: '2px',
                                height: '16px',
                                top: 17,
                                borderRadius: 0,
                                color: darkBlue,
                            },

                            '& .MuiSlider-mark': {
                                display: 'none',
                            },

                            '& .MuiSlider-markLabel': {
                                fontSize: 14,
                                color: darkBlue,
                                top: -30,
                            },

                            '& .MuiSlider-valueLabel': {
                                top: 45,
                                fontSize: 18,
                                fontWeight: 'bold',
                                backgroundColor: 'transparent',
                                color: darkBlue,
                            },
                        }}
                    />
                </Grid>}
                secondaryContent={
                    <Stack direction={'row'} spacing={2}>
                        <SmallIndicator
                            color={trajDeviationColor && `${trajDeviationColor}.contrastText`}
                            bgcolor={trajDeviationColor && `${trajDeviationColor}.main`}
                            borderColor={trajDeviationColor && `${trajDeviationColor}.main`}
                        >
                            {`${hasValue(consumptionTrajectoryDeviation) ? `${formatSign(round(consumptionTrajectoryDeviation, 1))}%` : '-'} ${i18n.comparedToForecast}`}
                        </SmallIndicator>
                        <SmallIndicator
                            color={needCoverageColor && `${needCoverageColor}.contrastText`}
                            bgcolor={needCoverageColor && `${needCoverageColor}.main`}
                            borderColor={needCoverageColor && `${needCoverageColor}.main`}
                        >
                            {hasValue(coverageRateOfNeeds) ? `${round(coverageRateOfNeeds, 1)}% ${i18n.ofNeedsCovered}` : i18n.unknownNeedsCoverage}
                        </SmallIndicator>
                    </Stack>
                }
            />
            <SummaryIndicator
                title={i18n.resources}
                bgColor='white'
                content={
                    <Grid size={6}>
                        <Select
                            label={i18n.referenceStations}
                            options={formattedLinkStationsDetails.map(({ name, idStation, dataType }) => ({ label: name, dataType, value: `${dataType}#${idStation}` }))}
                            value={selectedStation ? `${selectedStation.dataType}#${selectedStation.idStation}` : undefined}
                            onChange={v => {
                                const [dataType, idStation] = v.split('#').map(Number)
                                setSelectedStation({ idStation, dataType })
                            }}
                            sortFunc={({ dataType }) => -dataType}
                            noNullValue
                        />
                    </Grid>
                }
            />
            {isStationsLoaded ? (
                <ManagementNeedsChart
                    iaeauResults={iaeauResults}
                    vpLevel={withdrawableVolume}
                    vpLevelValues={vpLevelValues}
                    selectedStation={selectedStationDetails}
                />
            ) : <ProgressCard indeterminate />}
            <FollowAndPredictionConsoChart key={params.id} iaeauResults={iaeauResults} vpLevel={withdrawableVolume} vpLevelValues={vpLevelValues} />
            {isStationsLoaded ? (
                <FollowAndPredictionResourceChart selectedStation={selectedStationDetails} />
            ) : <ProgressCard indeterminate />}
            <ChangeVPLevelDialog
                open={openPopupSelectionVP}
                onClose={() => setOpenPopupSelectionVP(false)}
                managementUnitName={managementUnitName}
                defaultVpLevel={withdrawableVolume}
                vpLevelValues={vpLevelValues}
                estimatedNeed={estimatedNeed}
                recommendedVpLevel={2}
                linkStationsDetails={linkStationsDetails}
            />
        </Stack>
    )
}

export default TerritoryA3PDetail